// Header.jsx - WITH INTEGRATED SEARCH & ADVANCED SEARCH (JUMP & FOCUS FIX APPLIED)

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaBars, FaBell, FaChevronDown, FaSearch, FaTimes,
  FaUser, FaCog, FaSignOutAlt, FaEnvelope, FaStar, FaHeart,
  FaExclamationTriangle, FaCheckCircle, FaSpinner, FaSlidersH,
  FaMapMarkerAlt, FaBriefcase, FaCalendarAlt,
  FaUtensils, FaPlus, FaCheck
} from 'react-icons/fa';
import {
  getUserDashboard, getNotifications,
  markNotificationRead,
  deleteNotification,
  User_StateGet,
  getDistrictsByState,
} from '../../Api/CoreApi';

// ====================================================
// Searchable & Creatable Dropdown Component (With Scroll Jump Prevention)
// ====================================================
const SearchableMultiSelectDropdown = ({ label, icon, options, selected, onChange, onClear, placeholder, loading = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes(inputValue.toLowerCase())
  );

  const isExactMatchFound = options.some(opt => opt.toLowerCase() === inputValue.trim().toLowerCase());

  const handleAddCustom = (e) => {
    if (e) e.preventDefault();
    const val = inputValue.trim();
    if (val) {
      if (!selected.some(s => s.toLowerCase() === val.toLowerCase())) {
        onChange(val);
      }
      setInputValue('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddCustom();
    } else if (e.key === 'Backspace' && inputValue === '' && selected.length > 0) {
      onChange(selected[selected.length - 1]);
    }
  };

  return (
    <div ref={dropdownRef} className="hdr-ms-container">
      {label && (
        <label className="hdr-ms-label">
          {icon} {label}
          {loading && <FaSpinner style={{ animation: 'hdrSpin 1s linear infinite', marginLeft: 4 }} />}
        </label>
      )}
      <div
        className={`hdr-ms-box ${isOpen ? 'open' : ''}`}
        onClick={(e) => {
          if (e.target === e.currentTarget || e.target.classList.contains('hdr-ms-box')) {
            inputRef.current?.focus({ preventScroll: true });
          }
        }}
      >
        {selected.map(val => (
          <span key={val} className="hdr-ms-chip">
            {val}
            <span className="hdr-ms-chip-remove" onClick={(e) => { e.stopPropagation(); onChange(val); }}>
              <FaTimes />
            </span>
          </span>
        ))}

        <input
          ref={inputRef}
          type="text"
          className="hdr-ms-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={selected.length === 0 ? placeholder : ''}
        />

        {selected.length > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClear();
              inputRef.current?.focus({ preventScroll: true });
            }}
            style={{ background: 'none', border: 'none', color: '#ff0055', cursor: 'pointer', fontSize: 12, padding: '0 4px', marginLeft: 'auto' }}
          >
            <FaTimes />
          </button>
        )}
      </div>

      {isOpen && (
        <div className="hdr-ms-dropdown">
          {loading ? (
            <div style={{ padding: '12px', textAlign: 'center', color: '#ff0055', fontSize: 12 }}>
              <FaSpinner style={{ animation: 'hdrSpin 1s linear infinite' }} /> Loading...
            </div>
          ) : (
            <>
              {inputValue.trim() && !isExactMatchFound && (
                <div className="hdr-ms-add-custom" onClick={handleAddCustom}>
                  <FaPlus size={10} style={{ marginRight: 6 }} /> Add "{inputValue.trim()}"
                </div>
              )}

              {filteredOptions.length === 0 && !inputValue.trim() ? (
                <div style={{ padding: '12px', textAlign: 'center', color: '#999', fontSize: 12 }}>No options available</div>
              ) : (
                filteredOptions.map(opt => {
                  const isSelected = selected.includes(opt);
                  return (
                    <div
                      key={opt}
                      className={`hdr-ms-option ${isSelected ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(opt);
                        setInputValue('');
                        inputRef.current?.focus({ preventScroll: true });
                      }}
                    >
                      <div className={`hdr-ms-checkbox ${isSelected ? 'checked' : ''}`}>
                        {isSelected && <FaCheck />}
                      </div>
                      {opt}
                    </div>
                  )
                })
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const Header = ({ toggleSidebar, isMobile, sidebarOpen }) => {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Advanced filters state
  const [allStates, setAllStates] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);
  const [districtLoading, setDistrictLoading] = useState(false);
  const [isMasterDataLoading, setIsMasterDataLoading] = useState(false);
  const masterDataLoaded = useRef(false);

  const [advancedFilters, setAdvancedFilters] = useState({
    states: [],
    districts: [],
    motherTongues: [],
    foodStyles: [],
    jobs: [],
    educations: [],
    ageMin: '',
    ageMax: '',
    heightMin: '',
    heightMax: '',
    maritalStatus: 'all',
    showOnlyVerified: false,
    showOnlyOnline: false,
    showOnlyWithImages: false,
  });

  const notificationRef = useRef(null);
  const profileRef = useRef(null);
  const modalRef = useRef(null);
  const searchInputRef = useRef(null);
  const advancedSearchRef = useRef(null);

  useEffect(() => {
    fetchHeaderData();
    fetchNotifications();

    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target))
        setShowNotifications(false);
      if (profileRef.current && !profileRef.current.contains(e.target))
        setShowProfileMenu(false);
    };
    document.addEventListener('mousedown', handleClickOutside);

    const handleProfileUpdate = () => {
      fetchHeaderData();
    };
    window.addEventListener('profileUpdated', handleProfileUpdate);

    // ✅ NEW: Listen for reset from AllUsers page
    const handleHeaderReset = (e) => {
      if (e.detail) {
        setSearchTerm(e.detail.searchTerm || '');
        if (e.detail.advancedFilters) {
          setAdvancedFilters(e.detail.advancedFilters);
          setDistrictOptions([]);
        }
      }
    };
    window.addEventListener('headerFiltersReset', handleHeaderReset);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('profileUpdated', handleProfileUpdate);
      window.removeEventListener('headerFiltersReset', handleHeaderReset); // ✅ cleanup
    };
  }, []);

  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus({ preventScroll: true });
    }
  }, [showSearch]);

  // Load master data (states)
  useEffect(() => {
    if (masterDataLoaded.current) return;
    masterDataLoaded.current = true;
    const loadMasterData = async () => {
      setIsMasterDataLoading(true);
      try {
        const statesRes = await User_StateGet();
        setAllStates(Array.isArray(statesRes) ? statesRes.map(s => s?.state || s?.name || s).filter(Boolean) : []);
      } catch (err) { } finally {
        setIsMasterDataLoading(false);
      }
    };
    loadMasterData();
  }, []);

  // Rigid scroll lock helper on mobile keyboard layout shifts
  useEffect(() => {
    const isLocked = isMobile && (showNotifications || showAdvancedSearch);
    if (isLocked) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isMobile, showNotifications, showAdvancedSearch]);

  const loadDistricts = async (stateName) => {
    if (!stateName) { setDistrictOptions([]); return; }
    setDistrictLoading(true);
    try {
      const res = await getDistrictsByState(stateName);
      const districts = Array.isArray(res) ? res.map(d => d?.name || d?.district || d).filter(Boolean) : [];
      setDistrictOptions(prev => [...new Set([...prev, ...districts])]);
    } catch (err) { } finally { setDistrictLoading(false); }
  };

  const handleMultiSelect = async (key, value) => {
    const valStr = String(value).trim();
    const currentSelected = advancedFilters[key] || [];
    const isRemoving = currentSelected.some(v => v.toLowerCase() === valStr.toLowerCase());

    setAdvancedFilters(prev => {
      const current = prev[key] || [];
      if (isRemoving) {
        return { ...prev, [key]: current.filter(v => v.toLowerCase() !== valStr.toLowerCase()) };
      }
      return { ...prev, [key]: [...current, valStr] };
    });

    if (key === 'states' && !isRemoving) {
      await loadDistricts(valStr);
    }
  };

  const handleClearMultiSelect = (key) => {
    if (key === 'states') {
      setDistrictOptions([]);
      setAdvancedFilters(prev => ({ ...prev, states: [], districts: [] }));
    } else {
      setAdvancedFilters(prev => ({ ...prev, [key]: [] }));
    }
  };

  const handleClearAllFilters = () => {
    const emptyFilters = {
      states: [], districts: [], motherTongues: [], foodStyles: [], jobs: [], educations: [],
      ageMin: '', ageMax: '', heightMin: '', heightMax: '',
      maritalStatus: 'all', showOnlyVerified: false, showOnlyOnline: false, showOnlyWithImages: false
    };

    setAdvancedFilters(emptyFilters);
    setSearchTerm('');
    setDistrictOptions([]);

    // ✅ Clear session storage
    sessionStorage.removeItem('allUsersSearchParams');

    // ✅ Notify AllUsers page to reset filters immediately
    window.dispatchEvent(new CustomEvent('allUsersSearchApplied', {
      detail: {
        searchTerm: '',
        advancedFilters: emptyFilters
      }
    }));
  };

  const getActiveFilterCount = () => {
    return Object.entries(advancedFilters).filter(([k, v]) =>
      Array.isArray(v) ? v.length > 0 : (v !== '' && v !== 'all' && v !== false)
    ).length;
  };

  // Handle search submit - navigate to AllUsers with filters
  const handleApplySearch = () => {
    const searchParams = {
      searchTerm,
      advancedFilters
    };
    sessionStorage.setItem('allUsersSearchParams', JSON.stringify(searchParams));
    window.dispatchEvent(new CustomEvent('allUsersSearchApplied', { detail: searchParams }));
    navigate('/all-users');
    setShowAdvancedSearch(false);
    setShowSearch(false);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleApplySearch();
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotificationLoading(true);
      const data = await getNotifications(20);
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (error) {
      console.error('❌ Error fetching notifications:', error);
    } finally {
      setNotificationLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId = null) => {
    try {
      await markNotificationRead(notificationId);
      if (notificationId) {
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('❌ Error marking as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    try {
      await deleteNotification(notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('❌ Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      profile_view: '👀', new_profile: '🆕', connection_request: '💖',
      request_accepted: '✅', request_rejected: '❌', new_plan: '📦',
      message: '💬', shortlist: '⭐',
    };
    return icons[type] || '🔔';
  };

  const getNotificationLink = (notification) => {
    if (notification.link) return notification.link;
    switch (notification.notification_type) {
      case 'profile_view':
      case 'new_profile':
        return `/user-profile/${notification.from_user_id}`;
      case 'connection_request': return '/requests';
      case 'request_accepted': return `/messages?chat=${notification.from_user_id}`;
      case 'shortlist': return `/user-profile/${notification.from_user_id}`;
      case 'new_plan': return '/recharge';
      default: return '#';
    }
  };

  const fetchHeaderData = async () => {
    try {
      setLoading(true);
      const data = await getUserDashboard();
      let user = null;
      if (data?.currentUser) user = data.currentUser;
      else if (data?.user) user = data.user;
      else if (data?.data?.user) user = data.data.user;
      else if (data?.username || data?.name) user = data;
      setUserData(user || {});
    } catch (error) {
      const username = localStorage.getItem('username') || 'User';
      setUserData({ name: username });
    } finally {
      setLoading(false);
    }
  };

  const handleLogoutClick = () => {
    setShowProfileMenu(false);
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    ['access_token', 'refresh_token', 'user_id', 'profile_id',
      'username', 'role', 'refer', 'user', 'user_data'].forEach(k => localStorage.removeItem(k));
    setShowLogoutModal(false);
    navigate('/User_Login');
  };

  const handleLogoutCancel = () => setShowLogoutModal(false);

  const getDisplayName = () => {
    if (userData)
      return userData.username || userData.firstname || userData.name || userData.fullname || 'User';
    return localStorage.getItem('username') || 'User';
  };

  const getAvatarUrl = () => {
    if (userData?.image) return userData.image;
    const name = getDisplayName();
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=ff0055&color=fff&size=40`;
  };

  const getProfileId = () => {
    if (userData) {
      const profileId = userData.profile_id || userData.profileId || userData.profile;
      if (profileId) return profileId;
    }
    return (
      localStorage.getItem('profile_id') ||
      userData?.user_id || userData?.id ||
      localStorage.getItem('user_id') || ''
    );
  };

  const getUserId = () => {
    if (userData) return userData.user_id || userData.id || localStorage.getItem('user_id') || '';
    return localStorage.getItem('user_id') || '';
  };

  const displayName = getDisplayName();
  const avatarUrl = getAvatarUrl();
  const profileId = getProfileId();
  const userId = getUserId();
  const displayId = profileId || userId;

  const styles = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800;900&display=swap');

    *, *::before, *::after { box-sizing: border-box; }

    .dh-header {
      background: linear-gradient(135deg,#9b003d 0%,#c4004f 30%,#e91e63 65%,#ff5c93 100%);
      padding: 0 16px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: sticky;
      top: 0;
      z-index: 100;
      box-shadow: 0 4px 18px rgba(255,0,85,.18);
      font-family: 'Poppins', sans-serif;
      gap: 10px;
    }

    .dh-left { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
    .dh-center { display: flex; align-items: center; gap: 8px; flex: 1; max-width: 700px; justify-content: center; }
    .dh-right { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

    .dh-icon-btn {
      padding: 9px;
      background: rgba(255,255,255,.14);
      border: 1.5px solid rgba(255,255,255,.2);
      border-radius: 10px;
      cursor: pointer;
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .2s, transform .15s;
      flex-shrink: 0;
    }
    .dh-icon-btn:hover { background: rgba(255,255,255,.25); }
    .dh-icon-btn:active { transform: scale(.94); }

    /* Desktop Search Bar */
    .dh-search-desktop {
      position: relative;
      flex: 1;
      max-width: 400px;
    }
    .dh-search-icon-inner {
      position: absolute;
      left: 13px;
      top: 50%;
      transform: translateY(-50%);
      color: rgba(255,255,255,.7);
      pointer-events: none;
      font-size: 13px;
    }
    .dh-search-input {
      padding: 9px 14px 9px 38px;
      width: 100%;
      border: 1.5px solid rgba(255,255,255,.28);
      border-radius: 12px;
      font-size: 13px;
      outline: none;
      background: rgba(255,255,255,.14);
      backdrop-filter: blur(10px);
      color: #fff;
      font-family: 'Poppins', sans-serif;
      transition: border .25s, background .25s, box-shadow .25s;
    }
    .dh-search-input::placeholder { color: rgba(255,255,255,.65); }
    .dh-search-input:focus {
      border-color: rgba(255,255,255,.7);
      background: rgba(255,255,255,.2);
      box-shadow: 0 0 0 3px rgba(255,255,255,.12);
    }

    /* Advanced Search Button */
    .dh-adv-search-btn {
      padding: 9px 14px;
      background: rgba(255,255,255,.14);
      border: 1.5px solid rgba(255,255,255,.2);
      border-radius: 10px;
      cursor: pointer;
      color: #fff;
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      font-weight: 600;
      transition: all .2s;
      font-family: 'Poppins', sans-serif;
      position: relative;
      white-space: nowrap;
      flex-shrink: 0;
    }
    .dh-adv-search-btn:hover { background: rgba(255,255,255,.25); }
    .dh-adv-search-btn.active { background: rgba(255,255,255,.3); }
    .dh-filter-badge {
      background: #ffeb3b;
      color: #c4004f;
      font-size: 9px;
      font-weight: 800;
      padding: 2px 6px;
      border-radius: 999px;
      min-width: 16px;
      text-align: center;
    }

    /* Notification bell */
    .dh-notif-wrap { position: relative; }
    .dh-notif-btn { position: relative; }
    .dh-notif-pulse {
      position: absolute;
      top: 6px; right: 6px;
      width: 8px; height: 8px;
      background: #ffeb3b;
      border-radius: 50%;
      border: 2px solid #c4004f;
      animation: pulse 2s infinite;
    }
    .dh-notif-count {
      position: absolute;
      top: -5px; right: -5px;
      background: #f44336;
      color: #fff;
      font-size: 9px;
      font-weight: 800;
      padding: 2px 5px;
      border-radius: 999px;
      border: 2px solid #c4004f;
      min-width: 17px;
      text-align: center;
      line-height: 1.2;
      pointer-events: none;
    }
    @keyframes pulse {
      0%,100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.4); opacity: .8; }
    }

    /* Profile */
    .dh-profile-wrap { position: relative; }
    .dh-profile-btn {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 10px 4px 4px;
      background: rgba(255,255,255,.14);
      border: 1.5px solid rgba(255,255,255,.2);
      border-radius: 999px;
      cursor: pointer;
      transition: background .2s, transform .15s;
      backdrop-filter: blur(8px);
      flex-shrink: 0;
    }
    .dh-profile-btn:hover { background: rgba(255,255,255,.24); }
    .dh-profile-btn:active { transform: scale(.97); }
    .dh-avatar {
      width: 34px; height: 34px;
      border-radius: 50%;
      border: 2px solid rgba(255,255,255,.55);
      box-shadow: 0 3px 10px rgba(0,0,0,.15);
      object-fit: cover;
      flex-shrink: 0;
    }
    .dh-profile-name {
      font-size: 13px;
      font-weight: 700;
      color: #fff;
      max-width: 110px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .dh-profile-id-pill {
      font-size: 10px;
      color: rgba(255,255,255,.78);
      background: rgba(255,255,255,.12);
      padding: 2px 8px;
      border-radius: 20px;
      border: 1px solid rgba(255,255,255,.18);
      font-weight: 500;
      white-space: nowrap;
    }
    .dh-chevron { font-size: 10px; color: rgba(255,255,255,.7); }

    /* Dropdowns */
    .dh-dropdown {
      position: absolute;
      right: 0;
      top: calc(100% + 10px);
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 16px 48px rgba(155,0,61,.16);
      border: 1.5px solid #ffe4ec;
      z-index: 200;
      overflow: hidden;
      width: 390px;
    }
    .dh-dropdown-header {
      padding: 14px 18px;
      border-bottom: 1.5px solid #fff0f3;
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: linear-gradient(135deg,#fffafc,#fff0f3);
      flex-shrink: 0;
    }
    .dh-dropdown-title {
      margin: 0;
      font-size: 14.5px;
      font-weight: 800;
      color: #1a0a10;
      display: flex;
      align-items: center;
      gap: 7px;
      flex-wrap: wrap;
    }
    .dh-new-badge {
      font-size: 10.5px;
      color: #ff0055;
      font-weight: 700;
      background: #fff0f3;
      padding: 2px 10px;
      border-radius: 999px;
    }
    .dh-mark-all {
      font-size: 11.5px;
      color: #ff0055;
      background: none;
      border: none;
      cursor: pointer;
      font-weight: 700;
      white-space: nowrap;
      padding: 0;
    }
    .dh-mark-all:hover { color: #c2185b; }
    .dh-mark-all:disabled { opacity: .5; cursor: not-allowed; }

    .dh-dropdown-body {
      max-height: 400px;
      overflow-y: auto;
      overscroll-behavior: contain;
      -webkit-overflow-scrolling: touch;
    }
    .dh-dropdown-body::-webkit-scrollbar { width: 4px; }
    .dh-dropdown-body::-webkit-scrollbar-thumb {
      background: #ffc2d1;
      border-radius: 999px;
    }

    .dh-notif-item {
      padding: 12px 18px;
      border-bottom: 1px solid #fff0f3;
      cursor: pointer;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      transition: background .18s;
      position: relative;
    }
    .dh-notif-item:last-child { border-bottom: none; }
    .dh-notif-item:hover { background: #fffafc; }
    .dh-notif-item.unread { background: linear-gradient(90deg,#fff5f7,#fff); }
    .dh-notif-item.unread::before {
      content: '';
      position: absolute;
      left: 0; top: 0; bottom: 0;
      width: 4px;
      background: linear-gradient(180deg,#ff0055,#ff4d88);
      border-radius: 0 3px 3px 0;
    }
    .dh-notif-emoji {
      font-size: 19px;
      width: 38px; height: 38px;
      border-radius: 11px;
      background: linear-gradient(135deg,#fff0f3,#ffe4ec);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }
    .dh-notif-content { flex: 1; min-width: 0; }
    .dh-notif-text {
      font-size: 13px;
      margin: 0;
      color: #444;
      font-weight: 500;
      line-height: 1.45;
      word-break: break-word;
    }
    .dh-notif-text strong {
      display: block;
      color: #1a0a10;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .dh-notif-time {
      font-size: 11px;
      color: #bbb;
      margin: 5px 0 0;
      font-weight: 500;
    }
    .dh-notif-delete {
      background: none;
      border: none;
      color: #ddd;
      cursor: pointer;
      padding: 5px;
      margin: -5px -4px 0 0;
      transition: color .18s;
      flex-shrink: 0;
      border-radius: 6px;
    }
    .dh-notif-delete:hover { color: #f44336; background: #fef2f2; }

    .dh-empty-notif {
      padding: 44px 20px;
      text-align: center;
      color: #bbb;
      font-size: 14px;
    }
    .dh-empty-notif-icon {
      font-size: 46px;
      display: block;
      margin-bottom: 12px;
    }
    .dh-loading-notif {
      padding: 36px 20px;
      text-align: center;
      color: #ccc;
      font-size: 13px;
    }
    .dh-spinner { animation: hdrSpin 1s linear infinite; display: inline-block; }
    @keyframes hdrSpin { to { transform: rotate(360deg); } }

    .dh-dropdown-footer {
      padding: 12px;
      border-top: 1.5px solid #fff0f3;
      text-align: center;
      background: linear-gradient(135deg,#fffafc,#fff0f3);
      flex-shrink: 0;
    }
    .dh-view-all {
      font-size: 13px;
      color: #ff0055;
      font-weight: 700;
      cursor: pointer;
    }
    .dh-view-all:hover { color: #c2185b; }

    .dh-profile-dropdown { width: 250px; }
    .dh-profile-info { display: flex; align-items: center; gap: 10px; }
    .dh-profile-info-avatar {
      width: 38px; height: 38px;
      border-radius: 50%;
      border: 2.5px solid #ff0055;
      object-fit: cover;
      flex-shrink: 0;
    }
    .dh-profile-info-name {
      margin: 0;
      font-size: 13.5px;
      font-weight: 800;
      color: #1a0a10;
    }
    .dh-profile-info-id {
      margin: 2px 0 0;
      font-size: 11px;
      color: #ff4d88;
      font-weight: 600;
    }
    .dh-dropdown-menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 11px 18px;
      color: #555;
      text-decoration: none;
      font-size: 13.5px;
      font-weight: 600;
      transition: background .18s, color .18s;
      border: none;
      background: none;
      width: 100%;
      cursor: pointer;
      text-align: left;
      font-family: 'Poppins', sans-serif;
    }
    .dh-dropdown-menu-item:hover { background: #fff0f3; color: #ff0055; }
    .dh-dropdown-menu-item svg { color: #ff4d88; flex-shrink: 0; }
    .dh-dropdown-menu-item:hover svg { color: #ff0055; }
    .dh-menu-divider {
      border: none;
      border-top: 1.5px solid #fff0f3;
      margin: 4px 0;
    }
    .dh-logout-item { color: #f44336 !important; }
    .dh-logout-item:hover { background: #fef2f2 !important; color: #d32f2f !important; }
    .dh-logout-item svg { color: #f44336 !important; }

    .dh-notif-backdrop {
      display: none;
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.35);
      z-index: 150;
      animation: fadeIn .2s ease;
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

    /* Mobile Search Bar */
    .dh-mobile-search-bar {
      position: fixed;
      top: 60px;
      left: 0; right: 0;
      background: linear-gradient(135deg,#9b003d,#e91e63);
      padding: 12px 14px;
      box-shadow: 0 8px 20px rgba(155,0,61,.2);
      z-index: 99;
      animation: slideDown .25s ease;
      display: flex;
      gap: 8px;
      align-items: center;
    }
    @keyframes slideDown {
      from { transform: translateY(-100%); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .dh-mobile-search-wrap { position: relative; flex: 1; }
    .dh-mobile-search-icon-inner {
      position: absolute;
      left: 13px; top: 50%;
      transform: translateY(-50%);
      color: rgba(255,255,255,.7);
      pointer-events: none;
      font-size: 13px;
    }
    .dh-mobile-search-input {
      width: 100%;
      padding: 11px 40px 11px 38px;
      border: 1.5px solid rgba(255,255,255,.3);
      border-radius: 12px;
      font-size: 14px;
      outline: none;
      background: rgba(255,255,255,.15);
      color: #fff;
      font-family: 'Poppins', sans-serif;
    }
    .dh-mobile-search-input::placeholder { color: rgba(255,255,255,.65); }
    .dh-mobile-search-input:focus {
      border-color: rgba(255,255,255,.7);
      background: rgba(255,255,255,.22);
    }
    .dh-mobile-search-close {
      position: absolute;
      right: 12px; top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: rgba(255,255,255,.8);
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 4px;
    }
    .dh-mobile-search-apply {
      padding: 11px 16px;
      background: #fff;
      color: #c4004f;
      border: none;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      font-family: 'Poppins', sans-serif;
      flex-shrink: 0;
    }

    /* Advanced Search Panel */
    .dh-adv-search-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.5);
      z-index: 199;
      animation: fadeIn .2s ease;
    }
    .dh-adv-search-panel {
      position: fixed;
      top: 70px;
      right: 16px;
      width: 480px;
      max-width: calc(100vw - 32px);
      max-height: calc(100vh - 90px);
      background: #fff;
      border-radius: 20px;
      box-shadow: 0 20px 60px rgba(155,0,61,.25);
      border: 1.5px solid #ffe4ec;
      z-index: 200;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      animation: slideDownFade .25s ease;
    }
    @keyframes slideDownFade {
      from { transform: translateY(-10px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .dh-adv-search-header {
      padding: 16px 20px;
      background: linear-gradient(135deg,#fffafc,#fff0f3);
      border-bottom: 1.5px solid #fff0f3;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0;
    }
    .dh-adv-search-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 15px;
      font-weight: 800;
      color: #1a0a10;
      margin: 0;
    }
    .dh-adv-search-title svg { color: #ff0055; }
    .dh-adv-search-close {
      background: none;
      border: none;
      color: #999;
      cursor: pointer;
      padding: 6px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .2s, color .2s;
    }
    .dh-adv-search-close:hover { background: #fef2f2; color: #f44336; }
    
    .dh-adv-search-body {
      overflow-y: auto;
      padding: 16px 20px;
      flex: 1;
      overscroll-behavior: contain;
      scroll-behavior: auto !important; 
      -webkit-overflow-scrolling: touch;
    }
    .dh-adv-search-body::-webkit-scrollbar { width: 5px; }
    .dh-adv-search-body::-webkit-scrollbar-thumb {
      background: #ffc2d1;
      border-radius: 999px;
    }
    .dh-adv-section {
      font-size: 12px;
      font-weight: 800;
      color: #c2185b;
      margin: 14px 0 8px 0;
      padding-top: 10px;
      border-top: 1px dashed #ffe4ec;
      display: flex;
      align-items: center;
      gap: 6px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .dh-adv-section:first-child {
      margin-top: 0;
      padding-top: 0;
      border-top: none;
    }
    .dh-adv-section svg { color: #ff0055; font-size: 11px; }
    .dh-adv-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 12px;
    }
    .dh-adv-input, .dh-adv-select {
      width: 100%;
      padding: 10px 12px;
      border: 1.5px solid #ffe4ec;
      border-radius: 10px;
      font-size: 13px;
      outline: none;
      font-family: 'Poppins', sans-serif;
      transition: border .2s;
      background: #fff;
    }
    .dh-adv-input:focus, .dh-adv-select:focus { border-color: #ff0055; }
    .dh-adv-label {
      font-size: 10.5px;
      font-weight: 700;
      color: #c2185b;
      display: block;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .dh-adv-checkbox {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
      color: #555;
      cursor: pointer;
      padding: 8px 12px;
      border: 1.5px solid #ffe4ec;
      border-radius: 10px;
      transition: all .2s;
      font-weight: 500;
    }
    .dh-adv-checkbox:hover { background: #fff0f3; border-color: #ff4d88; }
    .dh-adv-checkbox input { cursor: pointer; accent-color: #ff0055; }
    .dh-adv-search-footer {
      padding: 14px 20px;
      border-top: 1.5px solid #fff0f3;
      background: linear-gradient(135deg,#fffafc,#fff0f3);
      display: flex;
      gap: 10px;
      flex-shrink: 0;
    }
    .dh-apply-btn {
      flex: 1;
      padding: 12px;
      background: linear-gradient(135deg,#ff0055,#ff4d88);
      color: #fff;
      border: none;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      font-family: 'Poppins', sans-serif;
      box-shadow: 0 6px 16px rgba(255,0,85,.25);
      transition: transform .15s, box-shadow .2s;
    }
    .dh-apply-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 20px rgba(255,0,85,.35); }
    .dh-apply-btn:active { transform: scale(.97); }
    .dh-clear-btn {
      padding: 12px 20px;
      background: #f5f5f5;
      color: #666;
      border: none;
      border-radius: 12px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
      font-family: 'Poppins', sans-serif;
      transition: background .2s;
    }
    .dh-clear-btn:hover { background: #eee; }

    .dh-range-row {
      display: flex;
      gap: 8px;
    }
    .dh-range-row .dh-adv-input { width: 50%; }

    /* Searchable Multiselect */
    .hdr-ms-container {
      position: relative;
      width: 100%;
    }
    .hdr-ms-label {
      font-size: 10.5px;
      font-weight: 700;
      color: #c2185b;
      display: block;
      margin-bottom: 4px;
      text-transform: uppercase;
      letter-spacing: 0.3px;
    }
    .hdr-ms-box {
      min-height: 40px;
      padding: 6px 10px;
      border: 1.5px solid #ffe4ec;
      border-radius: 10px;
      display: flex;
      flex-wrap: wrap;
      gap: 5px;
      align-items: center;
      cursor: text;
      background: #fff;
      transition: border .2s;
    }
    .hdr-ms-box.open, .hdr-ms-box:focus-within { border-color: #ff0055; }
    .hdr-ms-chip {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      padding: 3px 8px;
      background: linear-gradient(135deg,#fff0f3,#ffe4ec);
      color: #c2185b;
      border-radius: 999px;
      font-size: 11.5px;
      font-weight: 600;
    }
    .hdr-ms-chip-remove {
      cursor: pointer;
      display: flex;
      align-items: center;
      padding: 2px;
      border-radius: 50%;
      font-size: 9px;
      color: #ff0055;
    }
    .hdr-ms-chip-remove:hover { background: rgba(255,0,85,.15); }
    .hdr-ms-input {
      flex: 1;
      min-width: 80px;
      border: none;
      outline: none;
      font-size: 13px;
      background: transparent;
      font-family: 'Poppins', sans-serif;
      color: #333;
    }
    .hdr-ms-dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0; right: 0;
      max-height: 200px;
      overflow-y: auto;
      background: #fff;
      border: 1.5px solid #ffe4ec;
      border-radius: 10px;
      box-shadow: 0 8px 24px rgba(155,0,61,.12);
      z-index: 10;
    }
    .hdr-ms-dropdown::-webkit-scrollbar { width: 4px; }
    .hdr-ms-dropdown::-webkit-scrollbar-thumb { background: #ffc2d1; border-radius: 999px; }
    .hdr-ms-option {
      padding: 9px 12px;
      font-size: 12.5px;
      color: #555;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      transition: background .15s;
    }
    .hdr-ms-option:hover { background: #fff0f3; }
    .hdr-ms-option.selected { background: #fff5f7; color: #c2185b; font-weight: 600; }
    .hdr-ms-checkbox {
      width: 16px; height: 16px;
      border: 1.5px solid #ffc2d1;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 8px;
      color: #fff;
      flex-shrink: 0;
    }
    .hdr-ms-checkbox.checked {
      background: linear-gradient(135deg,#ff0055,#ff4d88);
      border-color: #ff0055;
    }
    .hdr-ms-add-custom {
      padding: 9px 12px;
      font-size: 12px;
      color: #ff0055;
      cursor: pointer;
      background: #fff5f7;
      border-bottom: 1px solid #ffe4ec;
      font-weight: 600;
      display: flex;
      align-items: center;
    }
    .hdr-ms-add-custom:hover { background: #ffe4ec; }

    /* Modal */
    .dh-modal-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0,0,0,.55);
      backdrop-filter: blur(6px);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 16px;
      animation: fadeIn .25s ease;
    }
    .dh-modal {
      background: #fff;
      border-radius: 24px;
      width: 100%;
      max-width: 420px;
      padding: 32px 28px 28px;
      box-shadow: 0 24px 64px rgba(0,0,0,.25);
      text-align: center;
      animation: slideUp .3s ease;
      position: relative;
    }
    @keyframes slideUp {
      from { transform: translateY(28px) scale(.96); opacity: 0; }
      to { transform: translateY(0) scale(1); opacity: 1; }
    }
    .dh-modal-close-btn {
      position: absolute;
      top: 14px; right: 14px;
      background: #f5f5f5;
      border: none;
      width: 32px; height: 32px;
      border-radius: 50%;
      font-size: 16px;
      color: #aaa;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background .2s, color .2s;
    }
    .dh-modal-close-btn:hover { background: #fef2f2; color: #f44336; }
    .dh-modal-icon {
      width: 72px; height: 72px;
      border-radius: 50%;
      background: linear-gradient(135deg,#fff0f3,#ffe4ec);
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      font-size: 32px;
      color: #f44336;
    }
    .dh-modal-title {
      font-size: 21px;
      font-weight: 800;
      color: #1a0a10;
      margin: 0 0 8px;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-subtitle {
      font-size: 14px;
      color: #888;
      margin: 0 0 20px;
      line-height: 1.6;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-user-chip {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      background: #fff5f7;
      border: 1.5px solid #ffe4ec;
      border-radius: 14px;
      padding: 10px 20px;
      margin-bottom: 22px;
    }
    .dh-modal-user-chip-name {
      font-size: 14px;
      font-weight: 700;
      color: #ff0055;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-user-chip-id {
      font-size: 11.5px;
      color: #aaa;
      font-family: 'Poppins', sans-serif;
    }
    .dh-modal-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
    }
    .dh-modal-btn {
      padding: 12px 28px;
      border: none;
      border-radius: 12px;
      font-size: 14px;
      font-weight: 700;
      cursor: pointer;
      transition: transform .2s, box-shadow .2s;
      font-family: 'Poppins', sans-serif;
      display: flex;
      align-items: center;
      gap: 8px;
      justify-content: center;
      min-width: 120px;
    }
    .dh-modal-btn:hover { transform: translateY(-2px); }
    .dh-modal-btn:active { transform: scale(.97); }
    .dh-modal-btn-cancel { background: #f5f5f5; color: #666; }
    .dh-modal-btn-cancel:hover { background: #eee; }
    .dh-modal-btn-logout {
      background: linear-gradient(135deg,#f44336,#d32f2f);
      color: #fff;
      box-shadow: 0 6px 18px rgba(244,67,54,.28);
    }
    .dh-modal-btn-logout:hover { box-shadow: 0 10px 28px rgba(244,67,54,.4); }

    /* RESPONSIVE */
    .hide-mobile { display: flex !important; }
    .show-mobile { display: none !important; }

    @media (max-width: 1024px) {
      .dh-search-desktop { max-width: 300px; }
      .dh-adv-search-btn .dh-adv-text { display: inline; }
    }

    @media (max-width: 900px) {
      .dh-center { max-width: 500px; }
      .dh-search-desktop { max-width: 250px; }
    }

        @media (max-width: 768px) {
    
      .dh-center { 
        justify-content: flex-end; 
        padding-right: 4px; 
      }
      .dh-center .dh-search-desktop { display: none !important; }
      .dh-adv-search-btn .dh-adv-text { display: none; }
      
      .dh-adv-search-btn { 
        padding: 9px; 
        width: 37px; 
        height: 37px; 
        justify-content: center;
      }
      
      .hide-mobile { display: none !important; }
      .show-mobile { display: flex !important; }

      .dh-adv-search-panel {
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        max-width: 100%;
        max-height: 85vh;
        border-radius: 22px 22px 0 0;
        animation: sheetUp .28s ease;
      }
      @keyframes sheetUp {
        from { transform: translateY(100%); }
        to { transform: translateY(0); }
      }

      .dh-notif-wrap > .dh-dropdown {
        position: fixed;
        top: auto;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        max-width: 100%;
        border-radius: 22px 22px 0 0;
        border-bottom: none;
        max-height: 80vh;
        display: flex;
        flex-direction: column;
        z-index: 201;
        animation: sheetUp .28s ease;
      }
      .dh-notif-backdrop { display: block; }
      .dh-dropdown-body { max-height: 55vh; }

      .dh-profile-wrap > .dh-dropdown {
        width: 240px;
        right: 0;
        position: absolute;
      }
    }

    @media (max-width: 480px) {
      .dh-header { padding: 0 12px; gap: 6px; }
      .dh-left { gap: 6px; }

      .dh-profile-btn { padding: 4px 8px 4px 4px; gap: 6px; }
      .dh-profile-name { display: none !important; }
      .dh-profile-id-pill { display: none !important; }
      .dh-chevron { display: none !important; }
      .dh-avatar { width: 32px; height: 32px; }

      .dh-right { gap: 5px; }

      .dh-notif-wrap > .dh-dropdown { max-height: 85vh; }
      .dh-dropdown-body { max-height: 60vh; }
      .dh-dropdown-header { padding: 13px 14px; }
      .dh-dropdown-title { font-size: 13.5px; }
      .dh-notif-item { padding: 11px 14px; gap: 10px; }
      .dh-notif-emoji { width: 34px; height: 34px; font-size: 17px; }
      .dh-notif-text { font-size: 12.5px; }
      .dh-notif-time { font-size: 10.5px; }

      .dh-profile-wrap > .dh-dropdown { width: calc(100vw - 16px); right: -6px; }

      .dh-modal { padding: 28px 20px 22px; border-radius: 20px; }
      .dh-modal-title { font-size: 19px; }
      .dh-modal-subtitle { font-size: 13px; }
      .dh-modal-btn { min-width: 100px; padding: 11px 18px; font-size: 13px; }
      .dh-modal-icon { width: 62px; height: 62px; font-size: 28px; }

      .dh-adv-search-header { padding: 14px 16px; }
      .dh-adv-search-body { padding: 14px 16px; }
      .dh-adv-search-footer { padding: 12px 16px; }
    }

    @media (max-width: 360px) {
      .dh-header { padding: 0 10px; }
      .dh-right { gap: 4px; }
      .dh-icon-btn { padding: 8px; }

      .dh-profile-wrap > .dh-dropdown {
        width: calc(100vw - 12px);
        right: -4px;
      }
      .dh-dropdown-menu-item { padding: 10px 14px; font-size: 13px; }

      .dh-modal-actions { flex-direction: column; }
      .dh-modal-btn { width: 100%; min-width: unset; }
    }
  `;

  return (
    <>
      <style>{styles}</style>

      <header className="dh-header">
        {/* LEFT */}
        <div className="dh-left">
          <button className="dh-icon-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <FaBars size={18} />
          </button>
        </div>

        {/* CENTER - Search */}
        <div className="dh-center">
          <div className="dh-search-desktop">
            <FaSearch className="dh-search-icon-inner" />
            <input
              type="text"
              placeholder="Search by name, location, caste..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              className="dh-search-input"
            />
          </div>

          <button
            className={`dh-adv-search-btn ${showAdvancedSearch ? 'active' : ''}`}
            onClick={() => setShowAdvancedSearch(v => !v)}
          >
            <FaSlidersH size={12} />
            <span className="dh-adv-text">Advanced</span>
            {getActiveFilterCount() > 0 && (
              <span className="dh-filter-badge">{getActiveFilterCount()}</span>
            )}
          </button>
        </div>

        {/* RIGHT */}
        <div className="dh-right">
          {/* Mobile search toggle */}
          <button
            className="dh-icon-btn show-mobile"
            onClick={() => setShowSearch(v => !v)}
            aria-label="Search"
          >
            {showSearch ? <FaTimes size={17} /> : <FaSearch size={16} />}
          </button>

          {/* NOTIFICATIONS */}
          <div className="dh-notif-wrap" ref={notificationRef}>
            <button
              className="dh-icon-btn dh-notif-btn"
              aria-label="Notifications"
              onClick={() => {
                const next = !showNotifications;
                setShowNotifications(next);
                if (next) fetchNotifications();
              }}
            >
              <FaBell size={17} />
              {unreadCount > 0 && (
                <>
                  <span className="dh-notif-pulse" />
                  <span className="dh-notif-count">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                </>
              )}
            </button>

            {showNotifications && (
              <div className="dh-notif-backdrop" onClick={() => setShowNotifications(false)} />
            )}

            {showNotifications && (
              <div className="dh-dropdown">
                <div className="dh-dropdown-header">
                  <h4 className="dh-dropdown-title">
                    🔔 Notifications
                    {unreadCount > 0 && <span className="dh-new-badge">{unreadCount} new</span>}
                  </h4>
                  {unreadCount > 0 && (
                    <button className="dh-mark-all" onClick={() => handleMarkAsRead()} disabled={notificationLoading}>
                      Mark all read
                    </button>
                  )}
                </div>

                <div className="dh-dropdown-body">
                  {notificationLoading ? (
                    <div className="dh-loading-notif">
                      <FaSpinner className="dh-spinner" size={22} color="#ff0055" />
                      <p style={{ marginTop: 10 }}>Loading notifications…</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    notifications.map(item => (
                      <div
                        key={item.id}
                        className={`dh-notif-item${!item.is_read ? ' unread' : ''}`}
                        onClick={() => {
                          if (!item.is_read) handleMarkAsRead(item.id);
                          const link = getNotificationLink(item);
                          if (link && link !== '#') {
                            navigate(link);
                            setShowNotifications(false);
                          }
                        }}
                      >
                        <div className="dh-notif-emoji">
                          {item.icon || getNotificationIcon(item.notification_type)}
                        </div>
                        <div className="dh-notif-content">
                          <p className="dh-notif-text">
                            <strong>{item.title}</strong>
                            {item.message}
                          </p>
                          <p className="dh-notif-time">{item.time_ago || 'Just now'}</p>
                        </div>
                        <button
                          className="dh-notif-delete"
                          onClick={e => { e.stopPropagation(); handleDeleteNotification(item.id); }}
                          aria-label="Delete notification"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))
                  ) : (
                    <div className="dh-empty-notif">
                      <span className="dh-empty-notif-icon">🔕</span>
                      No notifications yet
                    </div>
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="dh-dropdown-footer">
                    <span
                      className="dh-view-all"
                      onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                    >
                      View all notifications →
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* PROFILE */}
          <div className="dh-profile-wrap" ref={profileRef}>
            <button className="dh-profile-btn" onClick={() => setShowProfileMenu(v => !v)} aria-label="Profile menu">
              <img src={avatarUrl} alt={displayName} className="dh-avatar" />
              <span className="dh-profile-name">{displayName}</span>
              <span className="dh-profile-id-pill">#{displayId}</span>
              <FaChevronDown className="dh-chevron" size={10} />
            </button>

            {showProfileMenu && (
              <div className="dh-dropdown dh-profile-dropdown">
                <div className="dh-dropdown-header">
                  <div className="dh-profile-info">
                    <img src={avatarUrl} alt={displayName} className="dh-profile-info-avatar" />
                    <div>
                      <p className="dh-profile-info-name">{displayName}</p>
                      <p className="dh-profile-info-id">ID: #{displayId}</p>
                    </div>
                  </div>
                </div>

                <a href="/profile" className="dh-dropdown-menu-item"><FaUser size={14} /> My Profile</a>
                <a href="/shortlisted" className="dh-dropdown-menu-item"><FaStar size={13} /> Shortlisted</a>
                <a href="/messages" className="dh-dropdown-menu-item"><FaEnvelope size={13} /> Messages</a>
                <a href="/settings" className="dh-dropdown-menu-item"><FaCog size={13} /> Settings</a>
                <hr className="dh-menu-divider" />
                <button className="dh-dropdown-menu-item dh-logout-item" onClick={handleLogoutClick}>
                  <FaSignOutAlt size={13} /> Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* MOBILE SEARCH BAR */}
      {showSearch && (
        <div className="dh-mobile-search-bar">
          <div className="dh-mobile-search-wrap">
            <FaSearch className="dh-mobile-search-icon-inner" />
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search profiles..."
              className="dh-mobile-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleSearchKeyPress}
            />
            <button
              className="dh-mobile-search-close"
              onClick={() => setShowSearch(false)}
              aria-label="Close search"
            >
              <FaTimes size={16} />
            </button>
          </div>
          <button className="dh-mobile-search-apply" onClick={handleApplySearch}>
            Search
          </button>
        </div>
      )}

      {/* ADVANCED SEARCH PANEL (INLINED DIRECTLY TO AVOID JUMP & LOSE FOCUS) */}
      {showAdvancedSearch && (
        <>
          <div className="dh-adv-search-backdrop" onClick={() => setShowAdvancedSearch(false)} />
          <div className="dh-adv-search-panel" ref={advancedSearchRef}>
            <div className="dh-adv-search-header">
              <h3 className="dh-adv-search-title">
                <FaSlidersH /> Advanced Search
                {getActiveFilterCount() > 0 && (
                  <span className="dh-filter-badge">{getActiveFilterCount()}</span>
                )}
              </h3>
              <button className="dh-adv-search-close" onClick={() => setShowAdvancedSearch(false)}>
                <FaTimes size={14} />
              </button>
            </div>

            <div className="dh-adv-search-body">
              <div className="dh-adv-grid">
                {/* Search Term */}
                <div>
                  <label className="dh-adv-label">🔍 KEYWORD</label>
                  <input
                    type="text"
                    placeholder="Search name, caste, education..."
                    className="dh-adv-input"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={handleSearchKeyPress}
                  />
                </div>

                <div className="dh-adv-section"><FaMapMarkerAlt /> Location</div>

                <SearchableMultiSelectDropdown
                  label="States" icon="📍"
                  options={allStates}
                  selected={advancedFilters.states}
                  onChange={(val) => handleMultiSelect('states', val)}
                  onClear={() => handleClearMultiSelect('states')}
                  placeholder="Type or select state..."
                  loading={isMasterDataLoading}
                />

                <SearchableMultiSelectDropdown
                  label="Districts" icon="🏙️"
                  options={districtOptions}
                  selected={advancedFilters.districts}
                  onChange={(val) => handleMultiSelect('districts', val)}
                  onClear={() => handleClearMultiSelect('districts')}
                  placeholder={advancedFilters.states.length === 0 ? "Select state first" : "Type or select district..."}
                  loading={districtLoading}
                />

                <div className="dh-adv-section"><FaBriefcase /> Profession & Education</div>

                <SearchableMultiSelectDropdown
                  label="Jobs" icon="💼"
                  options={[]}
                  selected={advancedFilters.jobs}
                  onChange={(val) => handleMultiSelect('jobs', val)}
                  onClear={() => handleClearMultiSelect('jobs')}
                  placeholder="Type job (e.g. Engineer)..."
                />

                <SearchableMultiSelectDropdown
                  label="Education" icon="🎓"
                  options={[]}
                  selected={advancedFilters.educations}
                  onChange={(val) => handleMultiSelect('educations', val)}
                  onClear={() => handleClearMultiSelect('educations')}
                  placeholder="Type education (e.g. B.Tech)..."
                />

                <div className="dh-adv-section"><FaCalendarAlt /> Age & Height</div>

                <div>
                  <label className="dh-adv-label">🎂 AGE RANGE</label>
                  <div className="dh-range-row">
                    <input
                      type="number"
                      placeholder="Min"
                      className="dh-adv-input"
                      value={advancedFilters.ageMin}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, ageMin: e.target.value }))}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      className="dh-adv-input"
                      value={advancedFilters.ageMax}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, ageMax: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="dh-adv-label">📏 HEIGHT RANGE (cm)</label>
                  <div className="dh-range-row">
                    <input
                      type="number"
                      placeholder="Min"
                      className="dh-adv-input"
                      value={advancedFilters.heightMin}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, heightMin: e.target.value }))}
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      className="dh-adv-input"
                      value={advancedFilters.heightMax}
                      onChange={(e) => setAdvancedFilters(prev => ({ ...prev, heightMax: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="dh-adv-section"><FaUtensils /> Lifestyle</div>

                <SearchableMultiSelectDropdown
                  label="Mother Tongue" icon="🗣️"
                  options={['Hindi', 'English', 'Bengali', 'Tamil', 'Telugu', 'Marathi', 'Gujarati', 'Punjabi', 'Kannada', 'Malayalam', 'Urdu', 'Odia']}
                  selected={advancedFilters.motherTongues}
                  onChange={(val) => handleMultiSelect('motherTongues', val)}
                  onClear={() => handleClearMultiSelect('motherTongues')}
                  placeholder="Type or select language..."
                />

                <SearchableMultiSelectDropdown
                  label="Food Style" icon="🍽️"
                  options={['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan', 'Jain']}
                  selected={advancedFilters.foodStyles}
                  onChange={(val) => handleMultiSelect('foodStyles', val)}
                  onClear={() => handleClearMultiSelect('foodStyles')}
                  placeholder="Select food style..."
                />

                <div>
                  <label className="dh-adv-label">💍 MARITAL STATUS</label>
                  <select
                    className="dh-adv-select"
                    value={advancedFilters.maritalStatus}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, maritalStatus: e.target.value }))}
                  >
                    <option value="all">All</option>
                    <option value="Unmarried">Unmarried</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </select>
                </div>

                <div className="dh-adv-section"><FaCheckCircle /> Quick Filters</div>

                <label className="dh-adv-checkbox">
                  <input
                    type="checkbox"
                    checked={advancedFilters.showOnlyVerified}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, showOnlyVerified: e.target.checked }))}
                  /> ✅ Verified Only
                </label>

                <label className="dh-adv-checkbox">
                  <input
                    type="checkbox"
                    checked={advancedFilters.showOnlyOnline}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, showOnlyOnline: e.target.checked }))}
                  /> 🟢 Online Only
                </label>

                <label className="dh-adv-checkbox">
                  <input
                    type="checkbox"
                    checked={advancedFilters.showOnlyWithImages}
                    onChange={(e) => setAdvancedFilters(prev => ({ ...prev, showOnlyWithImages: e.target.checked }))}
                  /> 📸 With Photos Only
                </label>
              </div>
            </div>

            <div className="dh-adv-search-footer">
              <button className="dh-apply-btn" onClick={handleApplySearch}>
                🔍 Apply & Search
              </button>
              <button className="dh-clear-btn" onClick={handleClearAllFilters}>
                Clear
              </button>
            </div>
          </div>
        </>
      )}

      {/* LOGOUT MODAL */}
      {showLogoutModal && (
        <div className="dh-modal-overlay" onClick={handleLogoutCancel}>
          <div className="dh-modal" onClick={e => e.stopPropagation()} ref={modalRef}>
            <button className="dh-modal-close-btn" onClick={handleLogoutCancel} aria-label="Close">
              <FaTimes size={14} />
            </button>

            <div className="dh-modal-icon">
              <FaExclamationTriangle />
            </div>

            <h3 className="dh-modal-title">Logout?</h3>
            <p className="dh-modal-subtitle">
              You'll need to sign in again to access your account.
            </p>

            <div className="dh-modal-user-chip">
              <span className="dh-modal-user-chip-name">👤 {displayName}</span>
              <span className="dh-modal-user-chip-id">Profile ID: #{displayId}</span>
            </div>

            <div className="dh-modal-actions">
              <button className="dh-modal-btn dh-modal-btn-cancel" onClick={handleLogoutCancel}>
                <FaTimes size={13} /> Stay
              </button>
              <button className="dh-modal-btn dh-modal-btn-logout" onClick={handleLogoutConfirm}>
                <FaCheckCircle size={13} /> Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;